import React, { useCallback, useState } from 'react';
import { GetDemoDataStatusAPI, RemoveDemoDataAPI } from '../../api/API';
import { DataProviderInstance } from '../../api/DataProvider';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import Toast from '../support/Toast';
import { DEMO_DATA_REMOVED_EVENT } from './demoData';

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

const joinList = (parts) => (parts.length < 2
  ? parts.join('')
  : `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`);

/**
 * Texto da confirmação com tudo o que ainda existe de exemplo (GetDemoDataStatusAPI.counts:
 * patients, appointments, invoices, waiting_list, leads). Tipos sem nenhuma linha ficam fora.
 */
const describe = (counts) => {
  const value = (key) => Number(counts?.[key]);
  if (!Number.isFinite(value('patients')) || !Number.isFinite(value('appointments'))) {
    return 'Remove as pessoas, os atendimentos, os pagamentos, a lista de espera, os contatos interessados '
      + 'e as fichas de exemplo. Não dá para desfazer.';
  }
  const parts = [
    plural(value('patients'), 'pessoa', 'pessoas'),
    plural(value('appointments'), 'atendimento', 'atendimentos'),
  ];
  if (value('invoices') > 0) parts.push(plural(value('invoices'), 'pagamento', 'pagamentos'));
  if (value('waiting_list') > 0) parts.push(`${value('waiting_list')} na lista de espera`);
  if (value('leads') > 0) parts.push(plural(value('leads'), 'contato interessado', 'contatos interessados'));
  return `Remove ${joinList(parts)}, com as fichas e os anexos de exemplo. Não dá para desfazer.`;
};

// O aviso abre logo abaixo do topo (faixas + cabeçalho), sem cobrir a faixa. No desktop
// o topo é fixo e mede --app-top-bars-height; no celular, faixa do teste + cabeçalho.
const TOAST_SX = {
  top: 'calc(var(--app-top-bars-height, 0px) + 16px) !important',
  '@media (max-width: 1009px)': { top: '96px !important' },
};

/**
 * Confirmação de "Remover dados de exemplo", usada pela faixa do topo e pelo item
 * dos Primeiros passos. requestRemoval() busca as contagens e abre a confirmação;
 * depois de remover, recarrega a empresa e os pacientes e avisa o App para
 * remontar a tela atual e o sino para buscar o aviso (DEMO_DATA_REMOVED_EVENT).
 * element: popup + aviso, para renderizar no componente que usa o hook.
 * onRemoved é opcional.
 */
export default function useDemoDataRemoval(onRemoved) {
  const { GetCompany, GetPatients } = DataProviderInstance();
  const [description, setDescription] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [toast, setToast] = useState(null);

  const requestRemoval = useCallback(() => {
    if (removing) return;
    GetDemoDataStatusAPI((response) => setDescription(describe(response?.counts)));
  }, [removing]);

  const closeConfirm = useCallback(() => {
    if (!removing) setDescription(null);
  }, [removing]);

  const confirmRemoval = useCallback(() => {
    setRemoving(true);
    RemoveDemoDataAPI((response) => {
      setRemoving(false);
      setDescription(null);
      if (!response?.success) {
        setToast({ severity: 'error', message: 'Não foi possível remover os dados de exemplo. Tente novamente.' });
        return;
      }
      setToast({ severity: 'success', message: 'Dados de exemplo removidos. A conta agora só tem o que é seu.' });
      GetCompany();
      GetPatients(false);
      window.dispatchEvent(new CustomEvent(DEMO_DATA_REMOVED_EVENT));
      if (onRemoved) onRemoved(response);
    });
  }, [GetCompany, GetPatients, onRemoved]);

  const element = (
    <>
      <ConfirmDialog
        isOpen={description !== null}
        title="Remover dados de exemplo"
        message={description || ''}
        confirmLabel="Remover tudo"
        variant="danger"
        loading={removing}
        onConfirm={confirmRemoval}
        onCancel={closeConfirm}
      />
      <Toast
        open={!!toast}
        message={toast?.message || ''}
        severity={toast?.severity || 'success'}
        onClose={() => setToast(null)}
        sx={TOAST_SX}
      />
    </>
  );

  return { requestRemoval, removing, element };
}
